/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { uiTypes } from '@opengeoweb/store';
import type { AreaListItem, DrawingListItem } from './types';

export const areaDrawingDialogType = uiTypes.DialogTypes.AreaDrawingManager;
export const drawingDialogType = uiTypes.DialogTypes.DrawingTool;
export const areaDrawingLoaderType = uiTypes.DialogTypes.AreaDrawingLoader;

/**
 * Returns the id to identify a single feature of a drawing by. Drawings saved
 * with the drawing tool have a stable id on every feature (see
 * `ensureFeatureIds` in the layer reducer), but drawings saved before that was
 * introduced do not, so fall back to the position within the drawing.
 * @param drawing The drawing the feature belongs to
 * @param feature The feature to get the id for
 * @param index The index of the feature within the drawing
 * @returns id of the feature
 */
export const getDrawingFeatureId = (
  drawingId: string,
  feature: GeoJSON.Feature,
  index: number,
): string => String(feature.id ?? `${drawingId}-${index}`);

/**
 * Makes sure every feature of a drawing has an id, so single features can be
 * identified, selected and assigned.
 * @param drawing The drawing to add feature ids to
 * @returns the drawing, with an id on every feature
 */
export const withDrawingFeatureIds = <T extends DrawingListItem | AreaListItem>(
  drawing: T,
): T => ({
  ...drawing,
  geoJSON: {
    ...drawing.geoJSON,
    features: drawing.geoJSON.features.map((feature, index) => ({
      ...feature,
      id: getDrawingFeatureId(drawing.id, feature, index),
    })),
  },
});
